import type { FieldStates } from './fieldStates';
import type { TicketQuestion, QuestionAnswer } from '@/lib/tickets/questions';
import type { RedirectForm } from '@/lib/payment/types';
import type { SpecialtyOption } from '@/lib/specialties/types';
import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry';
import { profileErrorMessageKey } from '@/lib/profile/errorMessage';

/**
 * Browser side of the one registration path. The site (cookie session, with
 * the 401 retry) and the widget (Bearer token, or cookies when the browser
 * allows them) both talk to the same endpoints through these functions.
 */
export type RegistrationChannel = 'site' | 'embed';

export interface RegistrationNeeds {
  states: FieldStates;
  bprLocked: boolean;
  missingFields: string[];
  values: Record<string, string>;
  questions: TicketQuestion[];
}
export type NeedsResult = { ok: true; needs: RegistrationNeeds } | { ok: false; status: number };

export interface SiteCheckout { provider: string; widgetData: Record<string, unknown>; signature?: string; mode?: 'redirect' | 'widget'; redirect?: RedirectForm }
export interface EmbedCheckout { sessionId: string; provider: string; mode?: string }

export type RegistrationResponse =
  | { kind: 'registered'; enrollmentId: string }
  | { kind: 'offline_reserved'; enrollmentId: string; amount: number; currency: string | null }
  | { kind: 'offline_offer'; amount: number; currency: string; instructions: string }
  | { kind: 'checkout'; checkout: Record<string, unknown> }
  | {
      kind: 'error'; status: number; errorType: string | null;
      missingFields?: string[]; answerErrors?: Record<string, string>;
      // ANSWERS_NOT_STORED: the enrollment exists, only the answers failed.
      enrollmentId?: string;
    };

export interface RegistrationRequest {
  contentId: string; ticketId: string | null; promocodeId: string | null;
  answers: QuestionAnswer[]; reserveOffline?: boolean;
  channel: RegistrationChannel; lang: 'uk' | 'en'; referrerUrl?: string | null;
}

/** The needs a profile-completion style body (also the enrollment details body) describes. */
export interface NeedsBody {
  states?: FieldStates; bprLocked?: boolean; missingFields?: string[];
  userBasicData?: Record<string, string>; questions?: TicketQuestion[];
}
export function needsFromBody(data: NeedsBody): RegistrationNeeds {
  return {
    states: data.states ?? {},
    bprLocked: !!data.bprLocked,
    missingFields: data.missingFields ?? [],
    values: data.userBasicData ?? {},
    questions: data.questions ?? [],
  };
}

export function needsForm(needs: RegistrationNeeds): boolean {
  return needs.missingFields.length > 0 || needs.questions.length > 0;
}

/** The widget token belongs to the widget only; the site rides its cookie session. */
function bearer(channel: RegistrationChannel, token?: string | null): Record<string, string> {
  return channel === 'embed' && token ? { Authorization: `Bearer ${token}` } : {};
}

/** Site calls retry once after recovering the session; the widget calls plain fetch. */
function request(channel: RegistrationChannel, url: string, init: RequestInit): Promise<Response> {
  return channel === 'site' ? fetchWithAuthRetry(url, init) : fetch(url, init);
}

export async function fetchRegistrationNeeds(input: {
  channel: RegistrationChannel; contentId: string; ticketId: string | null; token?: string | null;
  // A ticket change: questions this enrollment already answered are left out (site only).
  enrollmentId?: string;
}): Promise<NeedsResult> {
  const { channel, contentId, ticketId, token, enrollmentId } = input;
  const content = encodeURIComponent(contentId);
  const ticket = ticketId ? encodeURIComponent(ticketId) : null;
  const siteQuery = [
    ticket ? `ticketId=${ticket}` : null,
    enrollmentId ? `enrollmentId=${encodeURIComponent(enrollmentId)}` : null,
  ].filter(Boolean).join('&');
  const url = channel === 'site'
    ? `/api/courses/${content}/profile-completion${siteQuery ? `?${siteQuery}` : ''}`
    : `/api/embed/profile-completion?contentId=${content}${ticket ? `&ticketId=${ticket}` : ''}`;
  let response: Response;
  try {
    response = await request(channel, url, { headers: bearer(channel, token) });
  } catch {
    return { ok: false, status: 0 };
  }
  if (!response.ok) return { ok: false, status: response.status };
  try {
    return { ok: true, needs: needsFromBody(await response.json()) };
  } catch {
    // The server answered, with a body that is not the needs: its failure, not the network's.
    return { ok: false, status: 500 };
  }
}

export async function postRegistration(req: RegistrationRequest, opts?: { token?: string | null }): Promise<RegistrationResponse> {
  const body = {
    contentId: req.contentId,
    ticketId: req.ticketId,
    promocodeId: req.promocodeId,
    answers: req.answers,
    reserveOffline: req.reserveOffline,
    channel: req.channel,
    referrerUrl: req.referrerUrl,
    lang: req.lang,
    checkoutLanguage: req.lang,
  };
  try {
    const response = await request(req.channel, '/api/registrations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer(req.channel, opts?.token) },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        kind: 'error',
        status: response.status,
        errorType: data.errorType ?? null,
        ...(data.missingFields ? { missingFields: data.missingFields } : {}),
        ...(data.answerErrors ? { answerErrors: data.answerErrors } : {}),
        ...(data.enrollmentId ? { enrollmentId: data.enrollmentId } : {}),
      };
    }
    switch (data.outcome) {
      case 'registered':
        return { kind: 'registered', enrollmentId: data.enrollmentId };
      case 'offline_reserved':
        return { kind: 'offline_reserved', enrollmentId: data.enrollmentId, amount: data.amount, currency: data.currency ?? null };
      case 'offline_offer':
        return { kind: 'offline_offer', amount: data.amount, currency: data.currency, instructions: data.instructions ?? '' };
      case 'checkout':
        return { kind: 'checkout', checkout: data.checkout };
      default:
        return { kind: 'error', status: response.status, errorType: null };
    }
  } catch {
    return { kind: 'error', status: 0, errorType: 'NETWORK' };
  }
}

export type TicketChangeResponse =
  | { kind: 'changed'; answersStored: boolean }
  | { kind: 'payment'; checkout: SiteCheckout }
  | { kind: 'requirements'; missingFields?: string[]; answerErrors?: Record<string, string> }
  | { kind: 'error'; errorCode: string | null; message: string | null };

/** A participant changing to another ticket of the event they are enrolled in (site only). */
export async function postTicketChange(input: {
  enrollmentId: string; newTicketId: string; language: string; answers: QuestionAnswer[];
}): Promise<TicketChangeResponse> {
  try {
    const response = await fetchWithAuthRetry(`/api/enrollments/${encodeURIComponent(input.enrollmentId)}/change-ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newTicketId: input.newTicketId, language: input.language, answers: input.answers }),
    });
    const data = (await response.json().catch(() => null)) ?? {};
    if (!response.ok) {
      if (response.status === 422 && (data.errorType === 'PROFILE_INCOMPLETE' || data.errorType === 'ANSWERS_INVALID')) {
        return {
          kind: 'requirements',
          ...(data.missingFields ? { missingFields: data.missingFields } : {}),
          ...(data.answerErrors ? { answerErrors: data.answerErrors } : {}),
        };
      }
      return { kind: 'error', errorCode: data.errorCode ?? null, message: data.error ?? null };
    }
    if (data.requiresPayment) {
      return {
        kind: 'payment',
        checkout: {
          provider: data.provider,
          widgetData: data.widgetData,
          ...(data.signature ? { signature: data.signature } : {}),
          ...(data.mode ? { mode: data.mode } : {}),
          ...(data.redirect ? { redirect: data.redirect } : {}),
        },
      };
    }
    return { kind: 'changed', answersStored: data.answersStored ?? true };
  } catch {
    return { kind: 'error', errorCode: null, message: null };
  }
}

/**
 * The translation key for a change-ticket refusal, or null for a code with no
 * copy (the caller then shows a generic translated message - never the
 * route's English text).
 */
export function ticketChangeErrorKey(code: string | null, has: (key: string) => boolean): string | null {
  if (!code) return null;
  const resolved = code === 'TICKET_SOLD_OUT' ? 'SOLD_OUT' : code;
  const candidates = [
    `crm.ticketChange.errors.${code}`,
    `registrationErrors.${resolved}`,
    `errors.payment.${resolved}`,
  ];
  return candidates.find(has) ?? null;
}

export async function saveRegistrationProfile(input: {
  channel: RegistrationChannel; contentId: string; update: Record<string, string>; token?: string | null;
}): Promise<{ ok: true } | { ok: false; errorKey: string }> {
  const { channel, contentId, update, token } = input;
  if (Object.keys(update).length === 0) return { ok: true };
  const fallbackKey = channel === 'site' ? 'errors.errorSavingData' : 'embed.error';
  try {
    let response: Response;
    if (channel === 'site') {
      // Same conversion AdditionalProfileFieldsForm does for PUT /api/profile.
      const typed: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(update)) {
        if (key === 'is_not_working') {
          typed[key] = value === 'true';
        } else if (key === 'secondary_specialty_ids') {
          try { typed[key] = JSON.parse(value || '[]'); } catch { typed[key] = []; }
        } else {
          typed[key] = value;
        }
      }
      response = await fetchWithAuthRetry('/api/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(typed),
      });
    } else {
      response = await fetch('/api/embed/profile-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...bearer(channel, token) },
        body: JSON.stringify({ fields: update, contentId }),
      });
    }
    if (response.ok) return { ok: true };
    const data = await response.json().catch(() => ({}));
    return { ok: false, errorKey: profileErrorMessageKey(data.error) ?? fallbackKey };
  } catch {
    return { ok: false, errorKey: fallbackKey };
  }
}

/** null when the catalog could not be loaded – not the same as an empty one. */
export async function fetchSpecialtyCatalog(): Promise<SpecialtyOption[] | null> {
  try {
    const response = await fetch('/api/specialties');
    if (!response.ok) return null;
    const data = await response.json();
    return Array.isArray(data) ? data : null;
  } catch {
    return null;
  }
}

export function registrationErrorKey(errorType: string | null, channel: RegistrationChannel): string {
  const site = channel === 'site';
  switch (errorType) {
    case 'ANSWERS_NOT_STORED':
      return site ? 'courseDetails.answersNotStoredError' : 'embed.answersNotStoredError';
    case 'ALREADY_ENROLLED':
      return site ? 'api.alreadyEnrolled' : 'embed.alreadyRegistered';
    case 'EVENT_NOT_AVAILABLE':
    case 'SOLD_OUT':
    case 'TICKET_NOT_ON_SALE':
    case 'PROMOCODE_REQUIRED':
    case 'PROMOCODE_INVALID':
    case 'TICKET_REQUIRED':
    case 'PAYMENT_REQUIRED':
    case 'OFFLINE_NOT_AVAILABLE':
    case 'ANSWERS_INVALID':
      return `registrationErrors.${errorType}`;
    case 'PAYMENT_NOT_CONFIGURED':
      return site ? 'errors.payment.PAYMENT_NOT_CONFIGURED' : 'embed.paymentUnavailable';
    case 'PAYMENT_INTENT_FAILED':
      return site ? 'errors.payment.PAYMENT_CREATE_FAILED' : 'embed.paymentFailed';
    default:
      return site ? 'courseDetails.failedToEnroll' : 'embed.error';
  }
}
